import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Download, Eye, Mail, Phone, Calendar, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface JobApplication {
  id: string;
  job_opening_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  cover_letter: string;
  resume_file_url: string;
  resume_file_name: string;
  application_status: string;
  applied_at: string;
  admin_notes: string | null;
  job_openings: {
    title: string;
    location: string;
    department: string;
  };
}

const JobApplicationsView: React.FC = () => {
  const { t } = useTranslation(['admin', 'careers']);
  const { toast } = useToast();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const { data, error } = await supabase
        .from('job_applications')
        .select(`
          *,
          job_openings (
            title,
            location,
            department
          )
        `)
        .order('applied_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast({
        title: t('admin:error'),
        description: t('admin:applications.fetchError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('job_applications')
        .update({ application_status: status })
        .eq('id', applicationId);

      if (error) throw error;

      setApplications(applications.map(app => 
        app.id === applicationId ? { ...app, application_status: status } : app
      ));

      toast({
        title: t('admin:success'),
        description: t('admin:applications.statusUpdated'),
      });
    } catch (error) {
      console.error('Error updating application status:', error);
      toast({
        title: t('admin:error'),
        description: t('admin:applications.updateError'),
        variant: 'destructive',
      });
    }
  };

  const updateApplicationNotes = async (applicationId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('job_applications')
        .update({ admin_notes: notes })
        .eq('id', applicationId);

      if (error) throw error;

      setApplications(applications.map(app => 
        app.id === applicationId ? { ...app, admin_notes: notes } : app
      ));

      toast({
        title: t('admin:success'),
        description: t('admin:applications.notesUpdated'),
      });
    } catch (error) {
      console.error('Error updating application notes:', error);
      toast({
        title: t('admin:error'),
        description: t('admin:applications.updateError'),
        variant: 'destructive',
      });
    }
  };

  const downloadResume = async (application: JobApplication) => {
    try {
      const { data, error } = await supabase.storage
        .from('job-applications')
        .download(application.resume_file_url.split('/').pop() || '');

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = application.resume_file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast({
        title: t('admin:error'),
        description: t('admin:applications.downloadError'),
        variant: 'destructive',
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'reviewed':
        return 'bg-blue-100 text-blue-800';
      case 'shortlisted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredApplications = statusFilter === 'all' 
    ? applications 
    : applications.filter(app => app.application_status === statusFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg">{t('admin:loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold">{t('admin:applications.title')}</h3>
          <p className="text-muted-foreground">{t('admin:applications.description')}</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t('admin:applications.filterByStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin:applications.allStatuses')}</SelectItem>
            <SelectItem value="pending">{t('admin:applications.statuses.pending')}</SelectItem>
            <SelectItem value="reviewed">{t('admin:applications.statuses.reviewed')}</SelectItem>
            <SelectItem value="shortlisted">{t('admin:applications.statuses.shortlisted')}</SelectItem>
            <SelectItem value="rejected">{t('admin:applications.statuses.rejected')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredApplications.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">{t('admin:applications.noApplications')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredApplications.map((application) => (
            <Card key={application.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <CardTitle className="text-lg">
                      {application.first_name} {application.last_name}
                    </CardTitle>
                    <CardDescription>
                      {t('admin:applications.appliedFor')}: {application.job_openings.title}
                    </CardDescription>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {application.email}
                      </div>
                      {application.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {application.phone}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(application.applied_at), 'MMM dd, yyyy')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(application.application_status)}>
                      {t(`admin:applications.statuses.${application.application_status}`)}
                    </Badge>
                    <Select
                      value={application.application_status}
                      onValueChange={(value) => updateApplicationStatus(application.id, value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{t('admin:applications.statuses.pending')}</SelectItem>
                        <SelectItem value="reviewed">{t('admin:applications.statuses.reviewed')}</SelectItem>
                        <SelectItem value="shortlisted">{t('admin:applications.statuses.shortlisted')}</SelectItem>
                        <SelectItem value="rejected">{t('admin:applications.statuses.rejected')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">{t('admin:applications.coverLetter')}</h4>
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {application.cover_letter}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadResume(application)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('admin:applications.downloadResume')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedApplication(application)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t('admin:applications.viewDetails')}
                  </Button>
                </div>

                {application.admin_notes && (
                  <div className="bg-muted p-3 rounded-md">
                    <h4 className="font-medium text-sm mb-1">{t('admin:applications.adminNotes')}</h4>
                    <p className="text-sm">{application.admin_notes}</p>
                  </div>
                )}

                <div>
                  <Textarea
                    placeholder={t('admin:applications.addNotes')}
                    defaultValue={application.admin_notes || ''}
                    onBlur={(e) => {
                      if (e.target.value !== application.admin_notes) {
                        updateApplicationNotes(application.id, e.target.value);
                      }
                    }}
                    className="text-sm"
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobApplicationsView;